import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Lock, Play, CheckCircle, AlertCircle, Loader2, FileBarChart,
  Clock, ChevronRight, History, Zap, ShieldCheck, Snowflake,
  Shield, AlertTriangle, Sparkles, Brain, Wrench, Gauge,
  TrendingUp, TrendingDown
} from 'lucide-react';

const STEP_LABELS = {
  freeze_period: { ar: 'تجميد الفترة', en: 'Freeze Period' },
  depreciation: { ar: 'إهلاك الأصول', en: 'Depreciation' },
  accruals: { ar: 'استحقاقات الرواتب', en: 'Payroll Accruals' },
  overhead: { ar: 'تقرير المصاريف العامة', en: 'Overhead Report' },
  final_reconciliation: { ar: 'تسوية بنكية نهائية', en: 'Final Bank Reconciliation' },
  snapshot: { ar: 'إنشاء Snapshot', en: 'Generate Snapshot' },
  close_period: { ar: 'إقفال الفترة', en: 'Lock Period' }
};

const stepIcon = (status) => {
  if (status === 'ok' || status === 'already') return <CheckCircle className="text-emerald-500" size={18} />;
  if (status === 'error') return <AlertCircle className="text-rose-500" size={18} />;
  if (status === 'skipped') return <Clock className="text-amber-500" size={18} />;
  return <Loader2 className="text-gray-400 animate-spin" size={18} />;
};

const PeriodStatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const map = {
    closed:  { label: t('monthendclosing.closed'),   cls: 'bg-rose-100 text-rose-700 border-rose-200' },
    frozen:  { label: t('monthendclosing.frozen'),   cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    open:    { label: t('monthendclosing.open'),    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    active:  { label: t('monthendclosing.active'),    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
  };
  const m = map[status] || map.open;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${m.cls}`}>
      {status === 'closed' && <Lock size={12} />}
      {status === 'frozen' && <Snowflake size={12} />}
      {!['closed', 'frozen'].includes(status) && <ShieldCheck size={12} />}
      {m.label}
    </span>
  );
};

const MonthEndClosing = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [runs, setRuns] = useState([]);
  const [periodStatus, setPeriodStatus] = useState(null);
  const [snapshotDetail, setSnapshotDetail] = useState(null);
  const [snapshotView, setSnapshotView] = useState('tb'); // tb | pnl | bs
  const [tower, setTower] = useState(null);                // P8.2 Control Tower result
  const [towerLoading, setTowerLoading] = useState(false);
  const [ai, setAi] = useState(null);                       // P8.3 AI bundle
  const [aiLoading, setAiLoading] = useState(false);
  const [predict, setPredict] = useState(null);              // P8.4 prediction
  const [predictLoading, setPredictLoading] = useState(false);
  // Patch 3 — orphan-diagnostic drill-down
  const [diagnose, setDiagnose] = useState(null);              // { orphan_journals, inactive_account_journals, null_account_lines }
  const [diagnoseLoading, setDiagnoseLoading] = useState(false);
  const [diagnoseOpen, setDiagnoseOpen] = useState({}); // { ORPHAN: bool, INACTIVE: bool, NULL: bool }
  const [warnOpen, setWarnOpen] = useState({}); // { EXPENSE_SPIKE: bool, ORPHAN_PAYMENTS: bool }

  const runDiagnose = useCallback(async () => {
    setDiagnoseLoading(true);
    try {
      const r = await axiosInstance.get(`/month-end/diagnose-orphans/${period}`);
      setDiagnose(r.data);
      toast.success(`🔎 ${r.data.orphan_journals?.count || 0} orphans · ${r.data.inactive_account_journals?.count || 0} inactive · ${r.data.null_account_lines?.count || 0} null-account lines`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Diagnose failed: ${msg}`);
    } finally {
      setDiagnoseLoading(false);
    }
  }, [period]);

  // Patch 4 — inline VAT filing from the BLOCKED card
  const [filingVat, setFilingVat] = useState(false);
  const fileVatForPeriod = async () => {
    setFilingVat(true);
    try {
      // 1) Find the prepared/draft return for this period.
      const list = await axiosInstance.get('/vat/returns');
      const items = list.data?.returns || [];
      const target = items.find(r => (r.period_name === period
                                  || (r.period_start || '').startsWith(period))
                                  && ['prepared', 'draft'].includes((r.status || '').toLowerCase()));
      if (!target) {
        toast.error(`No prepared VAT return found for ${period}. Open VAT Returns page and prepare it first.`);
        setFilingVat(false);
        return;
      }
      const r = await axiosInstance.post(`/vat/returns/${target.id}/file`);
      toast.success(`✅ VAT return for ${period} filed (ref: ${r.data?.reference_number || target.id.slice(0,8)})`);
      // Re-run the tower to refresh the page.
      await runTower();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`File VAT failed: ${msg}`);
    } finally {
      setFilingVat(false);
    }
  };

  // ─── Pending workflow actions (Feb 2026) ───────────────────────
  // openWorkflow: deep-link to the source document so the user can
  // approve/reject inline. Falls back to /my-tasks when we can't
  // route to a specific document.
  const openWorkflow = (sample) => {
    const route = sample.route_hint || '/my-tasks';
    const docId = sample.document_id;
    if (docId && route !== '/my-tasks') {
      window.location.href = `${route}?id=${encodeURIComponent(docId)}`;
    } else {
      window.location.href = '/my-tasks';
    }
  };

  // dismissWorkflow: cancels a workflow whose source document was
  // deleted (orphan). Admin-only on the backend; the button only
  // appears when ``broken_reference === true``.
  const dismissWorkflow = async (workflowId, ref) => {
    if (!workflowId) return;
    const ok = window.confirm(
      `سيتم إلغاء الـ workflow ${ref || workflowId.slice(0,8)} لأن وثيقته المصدر محذوفة. هل تريد المتابعة؟`,
    );
    if (!ok) return;
    try {
      await axiosInstance.post(`/workflows/${workflowId}/cancel`, {
        reason: 'Source document no longer exists (auto-dismissed from Month-End Tower)',
      });
      toast.success('✅ Workflow dismissed');
      await runTower();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Dismiss failed: ${msg}`);
    }
  };

  // ─── Warning row open handlers ────────────────────────────────
  // openExpenseSpike: deep-link to the source expense (or report).
  // openOrphanPayment: deep-link to the payment page.
  const openExpenseSpike = (sample) => {
    const route = sample.report_id ? '/expense-reports' : '/expenses';
    const id = sample.report_id || sample.expense_id;
    if (id) {
      window.location.href = `${route}?id=${encodeURIComponent(id)}`;
    } else {
      window.location.href = route;
    }
  };
  const openOrphanPayment = (sample) => {
    if (sample.payment_id) {
      window.location.href = `/payments?id=${encodeURIComponent(sample.payment_id)}`;
    } else {
      window.location.href = '/payments';
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axiosInstance.get('/month-end/snapshots'),
        axiosInstance.get('/month-end/runs?limit=10')
      ]);
      setSnapshots(s.data.snapshots || []);
      setRuns(r.data.runs || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadStatus = useCallback(async (p) => {
    if (!p?.match(/^\d{4}-\d{2}$/)) return;
    try {
      const r = await axiosInstance.get(`/month-end/status/${p}`);
      setPeriodStatus(r.data);
    } catch (e) { setPeriodStatus(null); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadStatus(period); }, [period, loadStatus]);

  const run = async (dryRun) => {
    if (!period.match(/^\d{4}-\d{2}$/)) return toast.error('Period format: YYYY-MM');
    setRunning(true); setResult(null);
    try {
      const r = await axiosInstance.post(`/month-end/run?period=${period}&dry_run=${dryRun}`);
      setResult(r.data);
      toast.success(dryRun
        ? t('monthendclosing.dry_run_completed')
        : t('monthendclosing.month_end_closed'));
      await loadAll();
      await loadStatus(period);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Closing failed');
    } finally { setRunning(false); }
  };

  const openSnapshot = async (p) => {
    try {
      const r = await axiosInstance.get(`/month-end/snapshots/${p}`);
      setSnapshotDetail(r.data);
      setSnapshotView('tb');
    } catch (e) {
      toast.error('Snapshot not found');
    }
  };

  const runTower = async () => {
    if (!period.match(/^\d{4}-\d{2}$/)) return toast.error('Period format: YYYY-MM');
    setTowerLoading(true); setTower(null);
    try {
      const r = await axiosInstance.get(`/month-end/control-tower/${period}`);
      setTower(r.data);
      const s = r.data.status;
      if (s === 'CLOSED') toast.success(`✅ ${period} مغلق نهائياً — لا يلزم إجراء`);
      else if (s === 'BLOCKED') toast.error(`🔴 BLOCKED — ${r.data.summary.critical_count} critical issue(s)`);
      else if (s === 'WARNING') toast(`🟡 WARNING — ${r.data.summary.warning_count} warning(s)`);
      else toast.success(`🟢 READY — Score ${r.data.score}/100`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Control Tower scan failed');
    } finally { setTowerLoading(false); }
  };

  const runAI = async (applyFixes) => {
    if (!period.match(/^\d{4}-\d{2}$/)) return toast.error('Period format: YYYY-MM');
    setAiLoading(true); setAi(null);
    try {
      const r = await axiosInstance.post(
        `/month-end/ai/run/${period}?apply_fixes=${applyFixes ? 'true' : 'false'}`
      );
      setAi(r.data);
      setTower(r.data.tower);
      const dec = r.data.decision;
      const applied = r.data.auto_fixes.counts.applied;
      if (dec === 'AUTO_PROCEED') toast.success(`🟢 AI: Ready (Score ${r.data.tower.score}/100)`);
      else if (dec === 'PROCEED_WITH_WARNING') toast(`🟡 AI: Proceed with caution`);
      else if (dec === 'BLOCK_WITH_AUTO_FIX_ATTEMPT')
        toast(`🔧 AI: ${applied} fix(es) applied — re-run to verify`);
      else toast.error(`🔴 AI: Manual review required`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI control failed');
    } finally { setAiLoading(false); }
  };

  const runPredict = async () => {
    if (!period.match(/^\d{4}-\d{2}$/)) return toast.error('Period format: YYYY-MM');
    setPredictLoading(true); setPredict(null);
    try {
      const r = await axiosInstance.get(`/month-end/predict/${period}`);
      setPredict(r.data);
      const p = r.data.prediction;
      if (p === 'READY') toast.success(`🔮 ${r.data.success_probability}% success — Ready`);
      else if (p === 'LOW_RISK') toast.success(`🔮 ${r.data.success_probability}% success — Low Risk`);
      else if (p === 'MEDIUM_RISK') toast(`🔮 ${r.data.success_probability}% success — Medium Risk`);
      else toast.error(`🔮 ${r.data.success_probability}% success — HIGH RISK`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Prediction failed');
    } finally { setPredictLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="month-end-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Lock size={24} className="text-rose-600" />{t('monthendclosing.month_end_closing')}</h1>
        {periodStatus && <PeriodStatusBadge status={periodStatus.status} />}
      </div>

      {/* Hero: One-Click Closing Card */}
      <div className="bg-gradient-to-br from-slate-900 via-rose-950 to-slate-900 rounded-2xl shadow-xl p-6 border border-rose-900/30">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <p className="text-rose-300 text-xs font-semibold uppercase tracking-wider mb-2">P5 Engine</p>
            <h2 className="text-2xl font-bold text-white mb-2">
              7 خطوات محاسبية في زر واحد
            </h2>
            <p className="text-slate-300 text-sm">
              Freeze → Depreciation → Accruals → Overhead → Final Reconciliation → Snapshot → Lock
            </p>
            <div className="flex flex-wrap items-end gap-3 mt-5">
              <div>
                <label className="text-xs text-rose-300 mb-1 block">{t('monthendclosing.period_yyyy_mm')}</label>
                <input
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  placeholder="2026-02"
                  className="bg-slate-800 border border-rose-900/50 text-white rounded-lg px-4 py-2 font-mono focus:ring-2 focus:ring-rose-500 outline-none"
                  data-testid="period-input"
                />
              </div>
              <button
                onClick={() => run(true)}
                disabled={running}
                data-testid="dry-run-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-blue-200 font-semibold disabled:opacity-50 transition"
              >
                <Play size={16} /> Dry Run
              </button>
              <button
                onClick={runTower}
                disabled={towerLoading || aiLoading}
                data-testid="control-tower-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white font-semibold disabled:opacity-50 transition shadow-lg shadow-indigo-900/30"
              >
                {towerLoading ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
                Control Tower
              </button>
              <button
                onClick={() => runAI(true)}
                disabled={aiLoading || towerLoading || predictLoading}
                data-testid="ai-control-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-violet-700 to-fuchsia-700 hover:from-violet-600 hover:to-fuchsia-600 text-white font-semibold disabled:opacity-50 transition shadow-lg shadow-violet-900/30"
              >
                {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Brain size={16} />}
                AI Control + Fix
              </button>
              <button
                onClick={runPredict}
                disabled={predictLoading || aiLoading || towerLoading}
                data-testid="predict-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-cyan-700 to-sky-700 hover:from-cyan-600 hover:to-sky-600 text-white font-semibold disabled:opacity-50 transition shadow-lg shadow-cyan-900/30"
              >
                {predictLoading ? <Loader2 size={16} className="animate-spin" /> : <Gauge size={16} />}
                Predict Close
              </button>
              <button
                onClick={() => run(false)}
                disabled={running || periodStatus?.status === 'closed' || (tower && !tower.summary?.can_close)}
                data-testid="execute-month-end-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-rose-600 to-orange-600 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 shadow-lg shadow-rose-900/40 transition"
              >
                {running ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                {periodStatus?.status === 'closed'
                  ? t('monthendclosing.period_closed')
                  : (tower && !tower.summary?.can_close)
                    ? t('monthendclosing.blocked_by_control_tower')
                    : t('monthendclosing.run_closing')}
              </button>
            </div>
            {periodStatus?.status === 'closed' && (
              <p className="text-xs text-rose-300 mt-3">{t('monthendclosing.this_period_is_already_closed')}</p>
            )}
          </div>

          {/* Quick KPIs from periodStatus */}
          <div className="space-y-2 text-sm">
            <div className="bg-slate-800/60 rounded-lg p-3 border border-rose-900/30">
              <p className="text-rose-300 text-xs">Last Run</p>
              <p className="text-white font-mono text-sm">
                {periodStatus?.last_run?.started_at ? new Date(periodStatus.last_run.started_at).toLocaleString('en') : '—'}
              </p>
              {periodStatus?.last_run && (
                <p className="text-xs text-slate-400 mt-1">by {periodStatus.last_run.started_by_name || '—'}</p>
              )}
            </div>
            <div className="bg-slate-800/60 rounded-lg p-3 border border-rose-900/30">
              <p className="text-rose-300 text-xs">Snapshot</p>
              {periodStatus?.has_snapshot && periodStatus.snapshot_summary?.trial_balance ? (
                <>
                  <p className="text-white font-mono text-sm">
                    D {Number(periodStatus.snapshot_summary.trial_balance.total_debit).toLocaleString()} ≈
                    C {Number(periodStatus.snapshot_summary.trial_balance.total_credit).toLocaleString()}
                  </p>
                  <p className="text-xs text-emerald-400 mt-1">
                    {periodStatus.snapshot_summary.trial_balance.balanced ? t('monthendclosing.balanced') : '⚠ Not balanced'}
                  </p>
                </>
              ) : (
                <p className="text-slate-400 text-sm">No snapshot yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* P8.4 — Predictive Closing card */}
      {predict && (
        <div
          data-testid="predict-result-card"
          className={`rounded-2xl shadow-xl border-2 p-6 ${
            predict.prediction === 'HIGH_RISK_BLOCKED' ? 'bg-gradient-to-br from-rose-50 to-red-50 border-rose-400' :
            predict.prediction === 'MEDIUM_RISK' ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-400' :
            predict.prediction === 'LOW_RISK' ? 'bg-gradient-to-br from-sky-50 to-cyan-50 border-sky-300' :
            predict.status === 'ERROR' ? 'bg-slate-50 border-slate-300' :
            'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-400'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${
              predict.prediction === 'HIGH_RISK_BLOCKED' ? 'bg-rose-200' :
              predict.prediction === 'MEDIUM_RISK' ? 'bg-amber-200' :
              predict.prediction === 'LOW_RISK' ? 'bg-sky-200' :
              'bg-emerald-200'
            }`}>
              <Gauge size={28} className={
                predict.prediction === 'HIGH_RISK_BLOCKED' ? 'text-rose-700' :
                predict.prediction === 'MEDIUM_RISK' ? 'text-amber-700' :
                predict.prediction === 'LOW_RISK' ? 'text-sky-700' :
                'text-emerald-700'
              } />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">🔮 Predictive Closing</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Period <b>{predict.period}</b> · Risk Model + 9-step Simulation
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className={`text-4xl font-black ${
                      predict.success_probability >= 80 ? 'text-emerald-600' :
                      predict.success_probability >= 50 ? 'text-amber-600' :
                      'text-rose-600'
                    }`} data-testid="success-probability">
                      {predict.success_probability}%
                    </div>
                    <div className="text-xs text-slate-500">success</div>
                  </div>
                  <span data-testid="prediction-class" className={`text-xs font-mono px-3 py-2 rounded-lg ${
                    predict.prediction === 'HIGH_RISK_BLOCKED' ? 'bg-rose-200 text-rose-900' :
                    predict.prediction === 'MEDIUM_RISK' ? 'bg-amber-200 text-amber-900' :
                    predict.prediction === 'LOW_RISK' ? 'bg-sky-200 text-sky-900' :
                    'bg-emerald-200 text-emerald-900'
                  }`}>{predict.prediction}</span>
                </div>
              </div>
              <p className="text-sm text-slate-700 mt-3 font-semibold">
                {predict.recommendation_ar} / {predict.recommendation}
              </p>
            </div>
          </div>

          {/* Risk factors */}
          {predict.risk_factors?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <TrendingUp size={14} /> Risk factors ({predict.risk_factors.length})
              </h4>
              <ul className="space-y-1 text-sm text-slate-700">
                {predict.risk_factors.map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-rose-500 mt-0.5">•</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Expected failures */}
          {predict.what_will_fail?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-bold text-rose-700 mb-2 flex items-center gap-2">
                <TrendingDown size={14} /> Expected failures
              </h4>
              <ul className="space-y-1 text-sm text-slate-700" data-testid="expected-failures">
                {predict.what_will_fail.map((f, i) => (
                  <li key={i} className="bg-white rounded p-2 border border-rose-200">
                    <span className="text-rose-700 font-semibold mr-1">⚠</span>{f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Simulation steps */}
          {predict.simulation?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-2">🧪 Step-by-step simulation</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="simulation-steps">
                {predict.simulation.map((s, i) => (
                  <div key={i}
                       className={`text-xs px-3 py-2 rounded-lg border ${
                         s.predicted === 'fail' ? 'bg-rose-50 border-rose-200 text-rose-800' :
                         s.predicted === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' :
                         'bg-emerald-50 border-emerald-200 text-emerald-800'
                       }`}>
                    <div className="font-mono font-semibold">{s.step}</div>
                    <div className="text-[10px] uppercase mt-0.5">
                      {s.predicted === 'fail' ? '⛔ FAIL' :
                       s.predicted === 'warning' ? '⚠ WARN' : '✓ OK'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* P8.3 — AI Control Tower + Auto-Fix card */}
      {ai && (
        <div
          data-testid="ai-result-card"
          className={`rounded-2xl shadow-xl border-2 p-6 ${
            ai.decision === 'BLOCK_AND_FIX' ? 'bg-gradient-to-br from-rose-50 to-fuchsia-50 border-rose-300' :
            ai.decision === 'BLOCK_WITH_AUTO_FIX_ATTEMPT' ? 'bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-300' :
            ai.decision === 'PROCEED_WITH_WARNING' ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300' :
            'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-300'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${
              ai.decision === 'AUTO_PROCEED' ? 'bg-emerald-200' :
              ai.decision === 'PROCEED_WITH_WARNING' ? 'bg-amber-200' :
              ai.decision === 'BLOCK_WITH_AUTO_FIX_ATTEMPT' ? 'bg-violet-200' :
              'bg-rose-200'
            }`}>
              <Brain size={28} className={
                ai.decision === 'AUTO_PROCEED' ? 'text-emerald-700' :
                ai.decision === 'PROCEED_WITH_WARNING' ? 'text-amber-700' :
                ai.decision === 'BLOCK_WITH_AUTO_FIX_ATTEMPT' ? 'text-violet-700' :
                'text-rose-700'
              } />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="text-2xl font-bold text-slate-800">🧠 AI Status</h3>
                <span data-testid="ai-decision" className={`text-xs font-mono px-2 py-1 rounded ${
                  ai.decision === 'AUTO_PROCEED' ? 'bg-emerald-200 text-emerald-800' :
                  ai.decision === 'PROCEED_WITH_WARNING' ? 'bg-amber-200 text-amber-800' :
                  ai.decision === 'BLOCK_WITH_AUTO_FIX_ATTEMPT' ? 'bg-violet-200 text-violet-800' :
                  'bg-rose-200 text-rose-800'
                }`}>{ai.decision}</span>
              </div>
              <p className="text-sm font-semibold text-slate-700">{ai.decision_meta?.label}</p>
              <p className="text-sm text-slate-600 mt-2">{ai.explanation?.summary_ar}</p>
              <p className="text-xs text-slate-500 mt-1 italic">{ai.explanation?.summary}</p>
              <p className="text-sm text-slate-700 mt-3"><b>Risk:</b> {ai.explanation?.risk_explanation}</p>
              <p className="text-sm text-emerald-700 mt-1"><b>Closing readiness:</b> {ai.explanation?.closing_readiness}</p>
            </div>
          </div>

          {/* Auto-fix narrative + counts */}
          {(ai.auto_fixes.counts.applied > 0 || ai.auto_fixes.counts.flagged > 0) && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Wrench size={16} className="text-violet-600" /> Auto-Fix Engine
              </h4>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-white rounded-lg p-3 border border-emerald-200 text-center" data-testid="fixes-applied">
                  <div className="text-2xl font-bold text-emerald-700">{ai.auto_fixes.counts.applied}</div>
                  <div className="text-xs text-slate-500">Applied</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200 text-center" data-testid="fixes-flagged">
                  <div className="text-2xl font-bold text-amber-700">{ai.auto_fixes.counts.flagged}</div>
                  <div className="text-xs text-slate-500">Flagged</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
                  <div className="text-2xl font-bold text-slate-500">{ai.auto_fixes.counts.skipped}</div>
                  <div className="text-xs text-slate-500">Skipped</div>
                </div>
              </div>
              {ai.auto_fixes.applied.length > 0 && (
                <ul className="space-y-1 text-xs text-slate-700">
                  {ai.auto_fixes.applied.slice(0, 5).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle size={12} className="text-emerald-600 mt-0.5" />
                      <span><b>{f.action}</b> → {f.target_collection}/{(f.target_id || '').slice(0, 8)}
                        {f.new_value && <span className="font-mono ml-1">({f.new_value})</span>}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Insights */}
          {ai.explanation?.insights?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-2">✨ Insights</h4>
              <ul className="space-y-2 text-sm text-slate-700">
                {ai.explanation.insights.slice(0, 5).map((ins, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className={`mt-1 inline-block w-2 h-2 rounded-full ${
                      ins.severity === 'critical' ? 'bg-rose-500' : 'bg-amber-500'
                    }`} />
                    <div>
                      <div className="text-xs font-mono text-slate-500">{ins.issue_type}</div>
                      <div className="text-xs">{ins.text}</div>
                      <div className="text-xs text-slate-500 italic mt-0.5">{ins.text_ar}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* CTA buttons */}
          <div className="mt-5 pt-4 border-t border-slate-200 flex gap-3">
            {ai.decision === 'BLOCK_WITH_AUTO_FIX_ATTEMPT' && (
              <button
                onClick={() => runAI(true)}
                disabled={aiLoading}
                data-testid="re-run-ai-btn"
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold transition disabled:opacity-50"
              >
                {aiLoading ? <Loader2 size={14} className="animate-spin inline" /> : '🔄'} Re-run AI
              </button>
            )}
            {ai.decision === 'AUTO_PROCEED' && (
              <button
                onClick={() => run(false)}
                disabled={running}
                data-testid="ai-auto-close-btn"
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition disabled:opacity-50"
              >
                <Zap size={14} className="inline mr-1" /> {ai.decision_meta?.cta}
              </button>
            )}
            {ai.decision === 'PROCEED_WITH_WARNING' && (
              <button
                onClick={() => run(false)}
                disabled={running}
                data-testid="ai-proceed-btn"
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold transition disabled:opacity-50"
              >
                <Zap size={14} className="inline mr-1" /> Proceed Anyway
              </button>
            )}
            {ai.decision === 'BLOCK_AND_FIX' && (
              <span className="text-sm text-rose-700 italic">
                Manual review required — open the affected pages and resolve before retrying.
              </span>
            )}
          </div>
        </div>
      )}

      {/* P8.2 — Control Tower result card */}
      {tower && (
        <div
          data-testid="control-tower-result"
          className={`rounded-2xl shadow-lg border-2 p-6 ${
            tower.status === 'CLOSED'  ? 'bg-emerald-50 border-emerald-400' :
            tower.status === 'BLOCKED' ? 'bg-rose-50 border-rose-300' :
            tower.status === 'WARNING' ? 'bg-amber-50 border-amber-300' :
            tower.status === 'ERROR' ? 'bg-slate-50 border-slate-300' :
            'bg-emerald-50 border-emerald-300'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className={`p-3 rounded-xl ${
                tower.status === 'CLOSED'  ? 'bg-emerald-200' :
                tower.status === 'BLOCKED' ? 'bg-rose-200' :
                tower.status === 'WARNING' ? 'bg-amber-200' :
                tower.status === 'ERROR' ? 'bg-slate-200' :
                'bg-emerald-200'
              }`}>
                {tower.status === 'CLOSED' ? <CheckCircle size={28} className="text-emerald-700" /> :
                 tower.status === 'BLOCKED' ? <AlertCircle size={28} className="text-rose-700" /> :
                 tower.status === 'WARNING' ? <AlertTriangle size={28} className="text-amber-700" /> :
                 tower.status === 'ERROR' ? <AlertCircle size={28} className="text-slate-700" /> :
                 <Sparkles size={28} className="text-emerald-700" />}
              </div>
              <div>
                <h3 className={`text-2xl font-bold ${
                  tower.status === 'CLOSED'  ? 'text-emerald-800' :
                  tower.status === 'BLOCKED' ? 'text-rose-800' :
                  tower.status === 'WARNING' ? 'text-amber-800' :
                  tower.status === 'ERROR' ? 'text-slate-800' :
                  'text-emerald-800'
                }`} data-testid="tower-status-heading">
                  {tower.status === 'CLOSED'  && '✅ الفترة مغلقة نهائياً / Period Closed'}
                  {tower.status === 'BLOCKED' && '🛡 Month-End Status: BLOCKED'}
                  {tower.status === 'WARNING' && '🛡 Month-End Status: WARNING'}
                  {tower.status === 'READY' && '🛡 Month-End Status: READY'}
                  {tower.status === 'ERROR' && '🛡 Month-End Status: ERROR'}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  Period <b>{tower.period}</b> ({tower.details?.start_date} → {tower.details?.end_date})
                  {tower.status === 'CLOSED'
                    ? ' · لا يلزم أي إجراء — الفترة مُختومة'
                    : ' · 4 layers scanned'}
                </p>
              </div>
            </div>
            <div className="text-right">
              {tower.status === 'CLOSED' ? (
                <div className="text-emerald-700 font-bold text-lg" data-testid="tower-closed-badge">
                  CLOSED
                </div>
              ) : (
                <>
                  <div className={`text-5xl font-black ${
                    tower.score >= 90 ? 'text-emerald-600' :
                    tower.score >= 70 ? 'text-amber-600' :
                    'text-rose-600'
                  }`} data-testid="tower-score">
                    {tower.score}
                  </div>
                  <div className="text-xs text-slate-500">/ 100</div>
                </>
              )}
            </div>
          </div>

          {/* Issue lists */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
            {tower.critical?.length > 0 && (
              <div data-testid="tower-critical-list">
                <h4 className="text-sm font-bold text-rose-800 mb-2 flex items-center justify-between">
                  <span>🔴 Critical ({tower.critical.length})</span>
                  <button
                    data-testid="run-diagnose-btn"
                    onClick={runDiagnose}
                    disabled={diagnoseLoading}
                    className="text-xs font-normal px-2 py-1 bg-rose-50 text-rose-700 rounded hover:bg-rose-100 border border-rose-200 disabled:opacity-50"
                  >
                    {diagnoseLoading ? '...' : 'Show affected journals'}
                  </button>
                </h4>
                <ul className="space-y-2">
                  {tower.critical.map((c, i) => {
                    const isOrphan = c.type === 'ORPHAN_JOURNALS';
                    const isVat    = c.type === 'VAT_RETURN_NOT_PREPARED'
                                  || c.type === 'VAT_RETURN_NOT_FILED';
                    const isWorkflows = c.type === 'PENDING_WORKFLOWS';
                    return (
                    <li key={i} className="text-sm bg-white rounded-lg p-3 border border-rose-200">
                      <div className="font-semibold text-rose-900">{c.title || c.type}</div>
                      <div className="text-xs text-slate-600 mt-1">{c.message}</div>
                      {c.count !== undefined && (
                        <div className="text-xs text-rose-700 mt-1 font-mono">count: {c.count}</div>
                      )}
                      {/* Inline VAT file button — Patch 4 (TRM) */}
                      {isVat && (
                        <button
                          data-testid="inline-file-vat-btn"
                          onClick={fileVatForPeriod}
                          disabled={filingVat}
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-md text-xs font-semibold disabled:opacity-50"
                        >
                          {filingVat ? <Loader2 size={12} className="animate-spin" /> : '📤'}
                          File VAT for {period}
                        </button>
                      )}
                      {/* Sample list — embedded by the validator (samples[]) */}
                      {Array.isArray(c.samples) && c.samples.length > 0 && (
                        <div className="mt-2 text-xs">
                          <button
                            data-testid={`expand-samples-${c.type}`}
                            onClick={() => setDiagnoseOpen({ ...diagnoseOpen, [c.type]: !diagnoseOpen[c.type] })}
                            className="text-rose-700 underline"
                          >
                            {diagnoseOpen[c.type] ? 'Hide' : 'Show'} {c.samples.length} sample(s)
                          </button>
                          {diagnoseOpen[c.type] && isWorkflows && (
                            <ul data-testid="pending-workflows-list" className="mt-2 space-y-2">
                              {c.samples.map((s, k) => (
                                <li
                                  key={k}
                                  data-testid={`pending-workflow-${k}`}
                                  className={`p-2.5 rounded border ${s.broken_reference ? 'bg-amber-50 border-amber-300' : 'bg-rose-50 border-rose-200'}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="font-mono text-[11px] text-slate-700 truncate">
                                        {s.document_ref || s.document_id?.slice(0, 8) || s.workflow_id?.slice(0, 8)}
                                        <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500">{s.document_type}</span>
                                        {s.amount != null && (
                                          <span className="ml-2 text-[10px] font-semibold text-rose-700">
                                            {Number(s.amount).toLocaleString()} SAR
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-[11px] text-slate-600 mt-0.5">
                                        {s.broken_reference ? (
                                          <span className="text-amber-700 font-semibold">
                                            ⚠ Broken reference — source document deleted
                                          </span>
                                        ) : (
                                          <>
                                            Waiting:{' '}
                                            <span className="font-semibold text-slate-800">
                                              {s.approver_name || s.approver_role || s.approver_id?.slice(0, 8) || 'Unassigned'}
                                            </span>
                                            {s.current_step && s.total_steps && (
                                              <span className="ml-1.5 text-slate-500">
                                                · Step {s.current_step}/{s.total_steps}
                                              </span>
                                            )}
                                          </>
                                        )}
                                        {s.age_days != null && (
                                          <span className={`ml-2 text-[10px] ${s.age_days > 7 ? 'text-rose-700 font-bold' : 'text-slate-500'}`}>
                                            · Age: {s.age_days}d
                                          </span>
                                        )}
                                        {s.escalated && (
                                          <span className="ml-2 text-[10px] text-amber-700 font-bold">
                                            ⬆ Escalated
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      {s.broken_reference ? (
                                        <button
                                          data-testid={`workflow-dismiss-${k}`}
                                          onClick={() => dismissWorkflow(s.workflow_id, s.document_ref)}
                                          className="px-2.5 py-1 text-[11px] font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded"
                                        >
                                          Dismiss
                                        </button>
                                      ) : (
                                        <button
                                          data-testid={`workflow-open-${k}`}
                                          onClick={() => openWorkflow(s)}
                                          className="px-2.5 py-1 text-[11px] font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded"
                                        >
                                          Open
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                          {diagnoseOpen[c.type] && !isWorkflows && (
                            <table className="w-full mt-1 text-[11px] font-mono border-collapse">
                              <thead><tr className="bg-rose-50">
                                <th className="border px-1 py-0.5 text-left">Journal #</th>
                                <th className="border px-1 py-0.5 text-left">Date</th>
                                <th className="border px-1 py-0.5 text-left">Missing Account ID</th>
                              </tr></thead>
                              <tbody>
                                {c.samples.map((s, k) => (
                                  <tr key={k}>
                                    <td className="border px-1 py-0.5">{s.journal_no || s.journal_id?.slice(0,8)}</td>
                                    <td className="border px-1 py-0.5">{s.date || s.journal_date}</td>
                                    <td className="border px-1 py-0.5 text-rose-700">{(s.missing_account_id || s.account_id || '').slice(0,28) || '(null)'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                      {/* Full diagnose drill-down (from /diagnose-orphans) — Patch 9 forensic */}
                      {isOrphan && diagnose?.orphan_journals?.journals?.length > 0 && (
                        <details className="mt-2 text-xs" open>
                          <summary className="cursor-pointer text-rose-800 font-semibold">
                            🔎 Full forensic list: {diagnose.orphan_journals.count} orphan journals
                          </summary>
                          <div className="overflow-x-auto">
                          <table className="w-full mt-2 text-[10px] font-mono border-collapse">
                            <thead><tr className="bg-rose-50">
                              <th className="border px-1 py-0.5 text-left">Collection</th>
                              <th className="border px-1 py-0.5 text-left">Journal #</th>
                              <th className="border px-1 py-0.5 text-left">Journal ID</th>
                              <th className="border px-1 py-0.5 text-left">Date</th>
                              <th className="border px-1 py-0.5 text-left">Company ID</th>
                              <th className="border px-1 py-0.5 text-left">Source</th>
                              <th className="border px-1 py-0.5 text-left">Writer</th>
                              <th className="border px-1 py-0.5 text-left">Missing Account ID</th>
                              <th className="border px-1 py-0.5 text-left">Name Hint</th>
                              <th className="border px-1 py-0.5 text-left">Number Hint</th>
                            </tr></thead>
                            <tbody>
                              {diagnose.orphan_journals.journals.map((r, k) => (
                                <tr key={k}>
                                  <td className="border px-1 py-0.5">{r.collection}</td>
                                  <td className="border px-1 py-0.5 font-bold">{r.journal_no}</td>
                                  <td className="border px-1 py-0.5 text-slate-500" title={r.journal_id}>{(r.journal_id || '').slice(0, 8)}…</td>
                                  <td className="border px-1 py-0.5">{r.journal_date}</td>
                                  <td className="border px-1 py-0.5 text-slate-500" title={r.company_id}>{(r.company_id || '∅').slice(0, 12)}</td>
                                  <td className="border px-1 py-0.5" title={`${r.reference_type || ''} · id=${r.reference_id || ''}`}>
                                    {r.reference ? `${r.reference_type || '?'}: ${r.reference}` : (r.auto ? 'auto' : '—')}
                                  </td>
                                  <td className="border px-1 py-0.5 text-slate-500">{r.writer || (r.created_by_name || '—')}</td>
                                  <td className="border px-1 py-0.5 text-rose-700" title={r.missing_account_id}>{r.missing_account_id}</td>
                                  <td className="border px-1 py-0.5">{r.account_name_hint || '—'}</td>
                                  <td className="border px-1 py-0.5">{r.account_number_hint || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </details>
                      )}
                    </li>
                  );})}
                </ul>
              </div>
            )}
            {tower.warning?.length > 0 && (
              <div data-testid="tower-warning-list">
                <h4 className="text-sm font-bold text-amber-800 mb-2">🟡 Warning ({tower.warning.length})</h4>
                <ul className="space-y-2">
                  {tower.warning.map((w, i) => {
                    const isInactive = w.type === 'INACTIVE_ACCOUNTS';
                    const isSpike    = w.type === 'EXPENSE_SPIKE';
                    const isOrphanPay = w.type === 'ORPHAN_PAYMENTS';
                    const expandable = (isSpike || isOrphanPay) && Array.isArray(w.samples) && w.samples.length > 0;
                    const isOpen = !!warnOpen[w.type];
                    return (
                    <li key={i} data-testid={`tower-warning-${w.type}`} className="text-sm bg-white rounded-lg p-3 border border-amber-200">
                      <div className="font-semibold text-amber-900">{w.title || w.type}</div>
                      <div className="text-xs text-slate-600 mt-1">{w.message}</div>
                      {w.count !== undefined && (
                        <div className="text-xs text-amber-700 mt-1 font-mono">count: {w.count}</div>
                      )}
                      {isSpike && (
                        <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                          المتوسط: {(w.average || 0).toLocaleString()} SAR · العتبة (3×): {(w.threshold || 0).toLocaleString()} SAR
                        </div>
                      )}
                      {expandable && (
                        <button
                          data-testid={`warning-expand-${w.type}`}
                          onClick={() => setWarnOpen({ ...warnOpen, [w.type]: !isOpen })}
                          className="mt-2 text-xs text-amber-800 underline font-semibold"
                        >
                          {isOpen ? 'إخفاء' : `عرض ${w.samples.length} عناصر`}
                        </button>
                      )}
                      {isSpike && isOpen && (
                        <ul data-testid="expense-spike-list" className="mt-2 space-y-1.5">
                          {w.samples.map((s, k) => (
                            <li
                              key={k}
                              data-testid={`expense-spike-${k}`}
                              className="bg-amber-50/60 rounded border border-amber-200 p-2 flex items-start justify-between gap-2"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="font-mono text-[11px] text-slate-800 truncate">
                                  <span className="font-bold">{s.expense_no || s.expense_id?.slice(0,8)}</span>
                                  <span className="ml-2 text-rose-700 font-semibold">{Number(s.amount).toLocaleString()} SAR</span>
                                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-bold">{s.x_avg}× المتوسط</span>
                                </div>
                                <div className="text-[11px] text-slate-600 mt-0.5 truncate">
                                  {s.date && <span>📅 {s.date}</span>}
                                  {s.vendor_name && <span className="ml-2">🏢 {s.vendor_name}</span>}
                                  {s.project_name && <span className="ml-2">🏗 {s.project_name}</span>}
                                  {s.category && <span className="ml-2">📂 {s.category}</span>}
                                </div>
                                {s.description && (
                                  <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={s.description}>📝 {s.description}</div>
                                )}
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  {s.created_by_name && <span>بواسطة: {s.created_by_name}</span>}
                                  {s.payment_status && <span className="ml-2">حالة الدفع: {s.payment_status}</span>}
                                </div>
                              </div>
                              <button
                                data-testid={`expense-spike-open-${k}`}
                                onClick={() => openExpenseSpike(s)}
                                className="px-2.5 py-1 text-[11px] font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded shrink-0"
                              >
                                فتح
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {isOrphanPay && isOpen && (
                        <ul data-testid="orphan-payment-list" className="mt-2 space-y-1.5">
                          {w.samples.map((s, k) => (
                            <li
                              key={k}
                              data-testid={`orphan-payment-${k}`}
                              className="bg-amber-50/60 rounded border border-amber-200 p-2 flex items-start justify-between gap-2"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="font-mono text-[11px] text-slate-800 truncate">
                                  <span className="font-bold">{s.payment_no || s.payment_id?.slice(0,8)}</span>
                                  <span className="ml-2 text-rose-700 font-semibold">{Number(s.amount).toLocaleString()} SAR</span>
                                  {s.direction && (
                                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-bold ${s.direction==='received'?'bg-emerald-100 text-emerald-800':'bg-rose-100 text-rose-800'}`}>
                                      {s.direction === 'received' ? '↓ مقبوضة' : '↑ مدفوعة'}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-600 mt-0.5 truncate">
                                  {s.date && <span>📅 {s.date}</span>}
                                  {(s.supplier_name || s.customer_name) && (
                                    <span className="ml-2">
                                      {s.supplier_name ? '🏢 ' : '👤 '}
                                      {s.supplier_name || s.customer_name}
                                    </span>
                                  )}
                                  {s.payment_method && <span className="ml-2">💳 {s.payment_method}</span>}
                                  {s.bank_account_code && <span className="ml-2">🏦 {s.bank_account_code}</span>}
                                </div>
                                {s.reference && (
                                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">مرجع: {s.reference}</div>
                                )}
                                {s.project_name && (
                                  <div className="text-[11px] text-slate-500 mt-0.5">🏗 {s.project_name}</div>
                                )}
                                {s.description && (
                                  <div className="text-[11px] text-slate-500 mt-0.5 truncate" title={s.description}>📝 {s.description}</div>
                                )}
                              </div>
                              <button
                                data-testid={`orphan-payment-open-${k}`}
                                onClick={() => openOrphanPayment(s)}
                                className="px-2.5 py-1 text-[11px] font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded shrink-0"
                              >
                                فتح
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {isInactive && diagnose?.inactive_account_journals?.journals?.length > 0 && (
                        <details className="mt-2 text-xs" open>
                          <summary className="cursor-pointer text-amber-800 font-semibold">
                            🔎 Full forensic list: {diagnose.inactive_account_journals.count} affected journals
                          </summary>
                          {/* Collections legend — where the data lives */}
                          {diagnose.collections && (
                            <div className="text-[10px] text-slate-600 mt-1 font-mono bg-slate-50 p-1.5 rounded border border-slate-200">
                              <div>📁 journals: <span className="text-slate-900">{diagnose.collections.journals}</span></div>
                              <div>📁 accounts (primary): <span className="text-slate-900">{diagnose.collections.accounts_primary}</span></div>
                              <div>📁 accounts (fallback): <span className="text-slate-900">{diagnose.collections.accounts_fallback}</span></div>
                              <div>🛡 tenant (active_company_id): <span className="text-slate-900">{diagnose.active_company_id}</span></div>
                            </div>
                          )}
                          <div className="overflow-x-auto">
                          <table className="w-full mt-2 text-[10px] font-mono border-collapse">
                            <thead><tr className="bg-amber-50">
                              <th className="border px-1 py-0.5 text-left">Collection</th>
                              <th className="border px-1 py-0.5 text-left">Journal #</th>
                              <th className="border px-1 py-0.5 text-left">Journal ID</th>
                              <th className="border px-1 py-0.5 text-left">Date</th>
                              <th className="border px-1 py-0.5 text-left">Company ID</th>
                              <th className="border px-1 py-0.5 text-left">Source</th>
                              <th className="border px-1 py-0.5 text-left">Writer</th>
                              <th className="border px-1 py-0.5 text-left">Account ID</th>
                              <th className="border px-1 py-0.5 text-left">Acct Collection</th>
                              <th className="border px-1 py-0.5 text-left">Acct #</th>
                              <th className="border px-1 py-0.5 text-left">Acct Name</th>
                              <th className="border px-1 py-0.5 text-left">Status</th>
                              <th className="border px-1 py-0.5 text-left">Action</th>
                            </tr></thead>
                            <tbody>
                              {diagnose.inactive_account_journals.journals.map((r, k) => (
                                <tr key={k}>
                                  <td className="border px-1 py-0.5">{r.collection}</td>
                                  <td className="border px-1 py-0.5 font-bold">{r.journal_no}</td>
                                  <td className="border px-1 py-0.5 text-slate-500" title={r.journal_id}>{(r.journal_id || '').slice(0, 8)}…</td>
                                  <td className="border px-1 py-0.5">{r.journal_date}</td>
                                  <td className="border px-1 py-0.5 text-slate-500" title={r.company_id}>{(r.company_id || '∅').slice(0, 12)}</td>
                                  <td className="border px-1 py-0.5" title={`${r.reference_type || ''} · id=${r.reference_id || ''}`}>
                                    {r.reference ? `${r.reference_type || '?'}: ${r.reference}` : (r.auto ? 'auto' : '—')}
                                  </td>
                                  <td className="border px-1 py-0.5 text-slate-500">{r.writer || (r.created_by_name || '—')}</td>
                                  <td className="border px-1 py-0.5 text-rose-700" title={r.inactive_account_id}>{(r.inactive_account_id || '').slice(0, 12)}…</td>
                                  <td className="border px-1 py-0.5 text-slate-500">{r.account_collection}</td>
                                  <td className="border px-1 py-0.5">{r.account_number}</td>
                                  <td className="border px-1 py-0.5">{r.account_name}</td>
                                  <td className="border px-1 py-0.5 text-amber-700">{r.account_status}</td>
                                  <td className="border px-1 py-0.5">
                                    <button
                                      data-testid={`reactivate-account-btn-${r.inactive_account_id}`}
                                      onClick={async () => {
                                        try {
                                          await axiosInstance.post(`/accounts/${r.inactive_account_id}/reactivate`);
                                          toast.success(`✅ Reactivated ${r.account_number} — ${r.account_name}`);
                                          await runDiagnose();
                                          await runTower();
                                        } catch (e) {
                                          toast.error(`Reactivate failed: ${e.response?.data?.detail || e.message}`);
                                        }
                                      }}
                                      className="px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-semibold whitespace-nowrap"
                                    >
                                      ✓ Reactivate
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        </details>
                      )}
                    </li>
                  );})}
                </ul>
                {/* Null-account-line drill-down — shown when diagnose was run */}
                {diagnose?.null_account_lines?.count > 0 && (
                  <details className="mt-3 text-xs bg-white rounded-lg p-3 border border-amber-200">
                    <summary className="cursor-pointer text-amber-800 font-semibold">
                      ⚠️ {diagnose.null_account_lines.count} line(s) with NO account_id
                    </summary>
                    <p className="text-[11px] text-slate-600 my-1">
                      Lines whose snapshot account_number maps to a real account will be re-linked silently by Auto-Fix. The rest will appear under UNRESOLVED_LINES (warning, not blocker).
                    </p>
                    <table className="w-full mt-2 text-[11px] font-mono border-collapse">
                      <thead><tr className="bg-amber-50">
                        <th className="border px-1 py-0.5 text-left">Journal #</th>
                        <th className="border px-1 py-0.5 text-left">Date</th>
                        <th className="border px-1 py-0.5 text-left">Line #</th>
                        <th className="border px-1 py-0.5 text-left">Debit</th>
                        <th className="border px-1 py-0.5 text-left">Credit</th>
                        <th className="border px-1 py-0.5 text-left">Description</th>
                      </tr></thead>
                      <tbody>
                        {diagnose.null_account_lines.journals.map((r, k) => (
                          <tr key={k}>
                            <td className="border px-1 py-0.5">{r.journal_no}</td>
                            <td className="border px-1 py-0.5">{r.journal_date}</td>
                            <td className="border px-1 py-0.5">{r.line_index}</td>
                            <td className="border px-1 py-0.5">{r.debit}</td>
                            <td className="border px-1 py-0.5">{r.credit}</td>
                            <td className="border px-1 py-0.5">{r.line_description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* Recommendations */}
          {tower.recommendations?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-2">✨ Recommended actions</h4>
              <ul className="space-y-1">
                {tower.recommendations.map((r, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                    <span className={`mt-0.5 inline-block w-2 h-2 rounded-full ${
                      r.severity === 'critical' ? 'bg-rose-500' : 'bg-amber-500'
                    }`} />
                    {r.action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tower.status === 'READY' && (
            <p className="mt-4 text-sm text-emerald-700 font-semibold">
              ✓ All checks passed. Ready to close.
            </p>
          )}
        </div>
      )}

      {/* Execution Result */}
      {result && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="run-result">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Zap size={18} className="text-rose-600" />
            نتيجة التنفيذ / Execution — {result.period}
            {result.dry_run && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-mono">DRY RUN</span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
              result.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
            }`}>{result.status}</span>
          </h2>
          <div className="space-y-2">
            {result.steps.map((s, i) => {
              const label = STEP_LABELS[s.step] || { ar: s.step, en: s.step };
              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-l from-gray-50 to-white border border-gray-100">
                  <div className="mt-0.5">{stepIcon(s.status)}</div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm text-gray-800">
                        {i + 1}. {label.ar} <span className="text-gray-400">/</span> {label.en}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                        s.status === 'ok' || s.status === 'already' ? 'bg-emerald-100 text-emerald-700' :
                        s.status === 'error' ? 'bg-rose-100 text-rose-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{s.status}</span>
                    </div>
                    {s.posted !== undefined && (
                      <p className="text-xs text-gray-600 mt-1">📦 Posted {s.posted} journals · Total: {s.total_amount?.toLocaleString()} SAR</p>
                    )}
                    {s.accrued_count !== undefined && (
                      <p className="text-xs text-gray-600 mt-1">💰 Accrued {s.accrued_count} payroll rows · Total: {s.accrued_total?.toLocaleString()} SAR · Skipped: {s.skipped}</p>
                    )}
                    {s.pool_amount !== undefined && (
                      <p className="text-xs text-gray-600 mt-1">🏗️ Overhead pool: {s.pool_amount?.toLocaleString()} SAR</p>
                    )}
                    {s.processed !== undefined && s.step === 'final_reconciliation' && (
                      <p className="text-xs text-gray-600 mt-1">🔗 Re-processed {s.processed} bank txs · Auto-matched: {s.auto_matched} · Review: {s.needs_review}</p>
                    )}
                    {s.balanced !== undefined && (
                      <div className="text-xs mt-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <span><strong className="text-gray-700">D:</strong> {s.total_debit?.toLocaleString()}</span>
                        <span><strong className="text-gray-700">C:</strong> {s.total_credit?.toLocaleString()}</span>
                        <span><strong className="text-gray-700">Net:</strong> <span className={s.net_income < 0 ? 'text-rose-600' : 'text-emerald-600'}>{s.net_income?.toLocaleString()}</span></span>
                        <span>{s.balanced ? '✓ Balanced' : '⚠ Not balanced'}</span>
                      </div>
                    )}
                    {s.note && <p className="text-xs text-blue-600 mt-1">ℹ️ {s.note}</p>}
                    {s.reason && <p className="text-xs text-gray-500 mt-1">↪ {s.reason}</p>}
                    {s.error && <p className="text-xs text-rose-600 mt-1">⚠ {s.error}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Snapshots list */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="font-bold text-base mb-3 flex items-center gap-2">
            <FileBarChart size={18} className="text-indigo-600" />
            Snapshots ({snapshots.length})
          </h2>
          {snapshots.length === 0 ? (
            <p className="text-gray-400 text-sm py-6 text-center">{t('monthendclosing.no_snapshots_yet')}</p>
          ) : (
            <div className="space-y-1">
              {snapshots.map((s) => (
                <button
                  key={s.period}
                  onClick={() => openSnapshot(s.period)}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-indigo-50 border border-gray-100 hover:border-indigo-200 transition group text-left"
                  data-testid={`snap-${s.period}`}
                >
                  <div className="flex-1">
                    <p className="font-mono font-bold text-sm text-gray-900">{s.period}</p>
                    {s.trial_balance ? (
                      <p className="text-xs text-gray-500">
                        D {Number(s.trial_balance.total_debit).toLocaleString()} · C {Number(s.trial_balance.total_credit).toLocaleString()}
                        {' · '}
                        {s.trial_balance.balanced ? '✓' : '⚠'}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        D {Number(s.total_debit || 0).toLocaleString()} · C {Number(s.total_credit || 0).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-gray-400 group-hover:text-indigo-600" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Run history */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="font-bold text-base mb-3 flex items-center gap-2">
            <History size={18} className="text-amber-600" />{t('monthendclosing.run_history')}</h2>
          {runs.length === 0 ? (
            <p className="text-gray-400 text-sm py-6 text-center">{t('monthendclosing.no_history_yet')}</p>
          ) : (
            <div className="space-y-2">
              {runs.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50">
                  <div>
                    <p className="font-mono text-sm font-semibold">{r.period}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(r.started_at).toLocaleString('en')} · {r.started_by_name || '—'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                      r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      r.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                      r.status === 'running' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{r.status}</span>
                    {r.dry_run && <p className="text-[10px] text-amber-600 mt-1 font-mono">DRY</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Snapshot Detail Modal */}
      {snapshotDetail && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
              <div>
                <h2 className="text-xl font-bold">Snapshot — {snapshotDetail.period}</h2>
                <p className="text-xs text-indigo-200">As of {snapshotDetail.as_of} · Snapped at {snapshotDetail.snapped_at?.split('T')[0]}</p>
              </div>
              <button onClick={() => setSnapshotDetail(null)} className="p-2 rounded-lg hover:bg-white/10" data-testid="close-snapshot">✕</button>
            </div>
            <div className="px-6 py-3 border-b flex gap-2">
              {[
                { k: 'tb', label: 'Trial Balance / ميزان المراجعة' },
                { k: 'pnl', label: 'P&L / الأرباح والخسائر' },
                { k: 'bs', label: 'Balance Sheet / الميزانية' }
              ].map(v => (
                <button
                  key={v.k}
                  onClick={() => setSnapshotView(v.k)}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition ${
                    snapshotView === v.k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`snap-view-${v.k}`}
                >{v.label}</button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {snapshotView === 'tb' && snapshotDetail.trial_balance && (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Account</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Type</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Debit</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshotDetail.trial_balance.rows.map((r, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-3 py-2">{r.account_name}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{r.account_type}</td>
                        <td className="px-3 py-2 text-right font-mono">{Number(r.debit || 0).toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{Number(r.credit || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr className="bg-indigo-50 font-bold">
                      <td className="px-3 py-2" colSpan="2">TOTAL</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(snapshotDetail.trial_balance.total_debit).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{Number(snapshotDetail.trial_balance.total_credit).toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              {snapshotView === 'pnl' && snapshotDetail.pnl && (
                <div className="space-y-4">
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    ⓘ القيم تراكمية حتى تاريخ {snapshotDetail.as_of}. /
                    Values are cumulative up to {snapshotDetail.as_of}. For period-only P&L use the live P&L report with date filters.
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-emerald-700 mb-2">Revenue / الإيرادات</h3>
                    <table className="min-w-full text-sm">
                      <tbody>
                        {snapshotDetail.pnl.revenue.map((r, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-3 py-2">{r.account_name}</td>
                            <td className="px-3 py-2 text-right font-mono">{Number(r.credit - r.debit).toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="bg-emerald-50 font-bold"><td className="px-3 py-2">Total Revenue</td><td className="px-3 py-2 text-right font-mono">{Number(snapshotDetail.pnl.total_revenue).toLocaleString()}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-rose-700 mb-2">Expenses / المصاريف</h3>
                    <table className="min-w-full text-sm">
                      <tbody>
                        {snapshotDetail.pnl.expense.map((r, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-3 py-2">{r.account_name}</td>
                            <td className="px-3 py-2 text-right font-mono">{Number(r.debit - r.credit).toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="bg-rose-50 font-bold"><td className="px-3 py-2">Total Expenses</td><td className="px-3 py-2 text-right font-mono">{Number(snapshotDetail.pnl.total_expense).toLocaleString()}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-xl p-4">
                    <p className="text-indigo-200 text-xs font-semibold uppercase">Net Income / صافي الدخل</p>
                    <p className="text-3xl font-bold font-mono mt-1">{Number(snapshotDetail.pnl.net_income).toLocaleString()} SAR</p>
                  </div>
                </div>
              )}
              {snapshotView === 'bs' && snapshotDetail.balance_sheet && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-bold text-sm text-blue-700 mb-2">Assets / الأصول</h3>
                    <table className="min-w-full text-sm">
                      <tbody>
                        {snapshotDetail.balance_sheet.assets.map((r, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-3 py-1.5">{r.account_name}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{Number(r.debit - r.credit).toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="bg-blue-50 font-bold"><td className="px-3 py-2">Total Assets</td><td className="px-3 py-2 text-right font-mono">{Number(snapshotDetail.balance_sheet.total_assets).toLocaleString()}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-bold text-sm text-rose-700 mb-2">Liabilities / الالتزامات</h3>
                      <table className="min-w-full text-sm">
                        <tbody>
                          {snapshotDetail.balance_sheet.liabilities.map((r, i) => (
                            <tr key={i} className="border-b">
                              <td className="px-3 py-1.5">{r.account_name}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{Number(r.credit - r.debit).toLocaleString()}</td>
                            </tr>
                          ))}
                          <tr className="bg-rose-50 font-bold"><td className="px-3 py-2">Total Liabilities</td><td className="px-3 py-2 text-right font-mono">{Number(snapshotDetail.balance_sheet.total_liabilities).toLocaleString()}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-emerald-700 mb-2">Equity / حقوق الملكية</h3>
                      <table className="min-w-full text-sm">
                        <tbody>
                          {snapshotDetail.balance_sheet.equity.map((r, i) => (
                            <tr key={i} className="border-b">
                              <td className="px-3 py-1.5">{r.account_name}</td>
                              <td className="px-3 py-1.5 text-right font-mono">{Number(r.credit - r.debit).toLocaleString()}</td>
                            </tr>
                          ))}
                          <tr className="bg-emerald-50 font-bold"><td className="px-3 py-2">Total Equity</td><td className="px-3 py-2 text-right font-mono">{Number(snapshotDetail.balance_sheet.total_equity).toLocaleString()}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthEndClosing;
